import { getFile } from '../../common/files/getFile';
import { toEpochMicros } from '../../common/files/toEpochMicros';
import useSWR from 'swr';

import {
  IIndirectTaxFilingV2DocumentWithFile,
  IndirectTaxFilingV2DocumentType,
} from './types';
import { useIndirectTaxFilingV2Documents } from './useIndirectTaxFilingV2Documents';

export function useIndirectTaxFilingV2DocumentsWithFiles(
  filingId: string | null | undefined,
  corporationId: number | null | undefined
) {
  const {
    documents,
    isLoading: isLoadingList,
    error: listError,
    mutate: mutateList,
  } = useIndirectTaxFilingV2Documents(filingId, corporationId);

  const fileIds = documents.map((d) => d.fileId);
  const fanOutKey =
    corporationId && fileIds.length > 0
      ? `indirect-tax-filing-v2-docs-files::${corporationId}::${fileIds.join(',')}`
      : null;

  const {
    data: resolvedFiles,
    isLoading: isResolvingFiles,
    error: filesError,
    mutate: mutateFiles,
  } = useSWR(fanOutKey, async () => {
    if (!corporationId) return [];
    const results = await Promise.all(
      fileIds.map((fileId) => getFile(fileId, corporationId))
    );
    return results.map((r, index) => ({
      fileId: fileIds[index],
      fileName: r.success ? r.data?.data.name ?? '' : '',
      signedUrl: r.success ? r.data?.data.signed_url ?? '' : '',
    }));
  });

  const documentsWithFiles: IIndirectTaxFilingV2DocumentWithFile[] = documents
    .map((doc): IIndirectTaxFilingV2DocumentWithFile | null => {
      const match = resolvedFiles?.find((f) => f.fileId === doc.fileId);
      if (!match) return null;
      return {
        ...doc,
        fileName: match.fileName,
        signedUrl: match.signedUrl,
        createdAtEpochMicros: toEpochMicros(doc.createdAt),
      };
    })
    .filter((d): d is IIndirectTaxFilingV2DocumentWithFile => d !== null);

  const confirmationDocuments = documentsWithFiles.filter(
    (d) => d.documentType === IndirectTaxFilingV2DocumentType.FILING_CONFIRMATION
  );
  const paymentDocuments = documentsWithFiles.filter(
    (d) => d.documentType === IndirectTaxFilingV2DocumentType.PAYMENT_CONFIRMATION
  );
  const returnDocuments = documentsWithFiles.filter(
    (d) => d.documentType === IndirectTaxFilingV2DocumentType.RETURN
  );

  return {
    documents: documentsWithFiles,
    confirmationDocuments,
    paymentDocuments,
    returnDocuments,
    isLoading: isLoadingList || isResolvingFiles,
    error: listError ?? filesError,
    mutate: async () => {
      await mutateList?.();
      await mutateFiles?.();
    },
  };
}
